import { Link } from "react-router";
import {
  ArrowRight,
  Bell,
  Camera,
  CheckCircle2,
  ClipboardCheck,
  Code2,
  FileImage,
  Globe,
  Link2,
  Mail,
  MessageCircle,
  MousePointerClick,
  Phone,
  Mic,
  Smartphone,
  SquareKanban,
  Users,
  Wrench,
} from "lucide-react";
import { FEATURES as FLAGS } from "@markfeed/shared";
import { Seo } from "@/components/seo";
import { ProductDemo } from "@/components/marketing/product-demo";
import { PricingTable } from "@/components/marketing/pricing-table";
import { Faq } from "@/components/marketing/faq";
import { Section } from "@/components/marketing/layout";
import { Button } from "@/components/ui/button";
import { SEO_PAGES } from "@/routes/seo";

const DEMO = "/review/demoabcfurniture2026review";

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[520px] bg-[linear-gradient(to_bottom,var(--color-brand-50),transparent)]" />
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:22px_22px] [mask-image:linear-gradient(to_bottom,black,transparent_70%)] opacity-40" />
      <div className="relative mx-auto grid max-w-6xl items-center gap-14 px-5 pb-20 pt-14 sm:pt-20 lg:grid-cols-[1fr_1.05fr]">
        <div className="animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3 py-1 text-[13px] text-slate-600 shadow-xs">
            <span className="size-1.5 rounded-full bg-green-500" /> Built for Indian agencies & freelancers
          </span>
          <h1 className="mt-5 text-[40px] font-semibold leading-[1.05] tracking-[-0.03em] text-ink sm:text-[56px]">
            Stop Chasing Client Feedback on <span className="relative whitespace-nowrap text-[#128C7E]">WhatsApp</span>
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-600">
            Let clients point, click and comment directly on your website. Keep every revision, screenshot and task in one place.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="xl" asChild>
              <Link to="/signup">
                Start Free <ArrowRight />
              </Link>
            </Button>
            <Button size="xl" variant="outline" asChild>
              <a href="#how-it-works">See How It Works</a>
            </Button>
          </div>
          <p className="mt-4 text-sm text-slate-500">
            Free forever plan · No card needed ·{" "}
            <Link to={DEMO} className="font-medium text-brand-600 hover:underline">
              Try the live demo →
            </Link>
          </p>
        </div>
        <ProductDemo className="lg:-mr-6" />
      </div>
    </section>
  );
}

function Bubble({ who, children, icon, tone = "in" }: { who?: string; children: React.ReactNode; icon?: React.ReactNode; tone?: "in" | "out" }) {
  return (
    <div className={tone === "in" ? "max-w-[85%]" : "ml-auto max-w-[85%]"}>
      <div className={`rounded-lg px-3 py-2 text-[13.5px] shadow-sm ${tone === "in" ? "rounded-tl-none bg-white" : "rounded-tr-none bg-[#DCF8C6]"}`}>
        {who && <p className="text-[11px] font-semibold text-[#128C7E]">{who}</p>}
        <p className="flex items-center gap-1.5 text-slate-800">
          {icon}
          {children}
        </p>
      </div>
    </div>
  );
}

function Problem() {
  return (
    <section className="border-y border-line bg-white">
      <div className="mx-auto grid max-w-6xl gap-12 px-5 py-20 sm:py-24 lg:grid-cols-2">
        <div>
          <p className="text-[13px] font-semibold uppercase tracking-wider text-red-600">Today</p>
          <h2 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">“Bhai ye thoda change karna hai”</h2>
          <p className="mt-4 text-[17px] leading-relaxed text-slate-600">
            Then come the WhatsApp screenshots, the voice notes, a phone call, an email, and a random photo of the laptop screen. You spend the
            morning figuring out <em>which</em> button on <em>which</em> page on <em>which</em> phone.
          </p>
          <div className="light-island mt-6 space-y-2.5 rounded-2xl bg-[#ECE5DD] p-4" aria-label="Example of scattered WhatsApp feedback">
            <Bubble who="Client (ABC Furniture)">bhai ye thoda change karna hai</Bubble>
            <Bubble who="Client (ABC Furniture)" icon={<Camera className="size-3.5 text-slate-500" />}>
              IMG_20261003_1142.jpg
            </Bubble>
            <Bubble who="Client (ABC Furniture)" icon={<Mic className="size-3.5 text-slate-500" />}>
              Voice message · 1:47
            </Bubble>
            <Bubble who="Client (ABC Furniture)">Logo thoda left karo. Aur mobile pe ye break ho raha hai</Bubble>
            <Bubble tone="out">Which page sir? 🙏</Bubble>
            <div className="flex flex-wrap gap-2 pt-1 text-[12px] text-slate-600">
              <span className="flex items-center gap-1 rounded-full bg-white/70 px-2 py-1">
                <Phone className="size-3" /> Missed call ×3
              </span>
              <span className="flex items-center gap-1 rounded-full bg-white/70 px-2 py-1">
                <Mail className="size-3" /> “Re: Re: Fwd: changes”
              </span>
            </div>
          </div>
        </div>
        <div className="lg:pt-0">
          <p className="text-[13px] font-semibold uppercase tracking-wider text-green-600">With MarkFeed</p>
          <h2 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">The client clicks the exact spot.</h2>
          <p className="mt-4 text-[17px] leading-relaxed text-slate-600">
            They leave the comment right on the element. You see exactly what needs to change — with the page, screenshot, device and browser attached.
          </p>
          <div className="mt-6 overflow-hidden rounded-2xl border border-line bg-slate-50">
            <div className="flex items-center gap-2 border-b border-line bg-white px-4 py-3">
              <span className="flex size-6 items-center justify-center rounded-full bg-brand-600 text-[11px] font-bold text-white">12</span>
              <p className="text-sm font-medium">Please reduce the heading size on mobile.</p>
            </div>
            <dl className="grid grid-cols-[110px_1fr] gap-y-2 px-4 py-4 text-[13px]">
              <dt className="text-slate-500">URL</dt>
              <dd className="font-mono text-[12.5px]">/pricing</dd>
              <dt className="text-slate-500">Element</dt>
              <dd>Pricing CTA</dd>
              <dt className="text-slate-500">Viewport</dt>
              <dd>390 × 844</dd>
              <dt className="text-slate-500">Device</dt>
              <dd className="flex items-center gap-1.5">
                <Smartphone className="size-3.5 text-slate-400" /> Mobile · Chrome · Android
              </dd>
              <dt className="text-slate-500">Assigned</dt>
              <dd>Rahul · Priority: Medium</dd>
              <dt className="text-slate-500">Status</dt>
              <dd>
                <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">● In Progress</span>
              </dd>
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { icon: Globe, title: "Add your website", text: "Paste a staging or live URL. Works with WordPress, Shopify, React, Next.js or plain HTML." },
  { icon: Link2, title: "Share the review link", text: "One tap to send on WhatsApp or email. Clients just enter their name — no signup." },
  { icon: MousePointerClick, title: "Client clicks & comments", text: "They click any element, type what to change and pick a priority. Screenshot captured automatically." },
  { icon: Wrench, title: "Your team fixes it", text: "Each comment becomes a task. Assign it, move it across the board, reply in the thread." },
  { icon: ClipboardCheck, title: "Client approves", text: "Client marks items resolved and approves the page. Everything is on record." },
];

function HowItWorks() {
  return (
    <Section id="how-it-works" eyebrow="How it works" title="From “change this” to “approved” in five steps" intro="The simplest possible workflow — designed for clients who are not technical and teams who are busy.">
      <ol className="grid gap-4 md:grid-cols-5">
        {STEPS.map((s, i) => (
          <li key={s.title} className="relative rounded-xl border border-line bg-white p-5">
            <span className="text-xs font-semibold text-slate-400">0{i + 1}</span>
            <s.icon className="mt-3 size-6 text-brand-600" />
            <h3 className="mt-3 font-semibold">{s.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{s.text}</p>
          </li>
        ))}
      </ol>
    </Section>
  );
}

function InteractiveDemo() {
  return (
    <section className="bg-ink text-white">
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-20 sm:py-24 lg:grid-cols-[1fr_1.1fr]">
        <div>
          <p className="text-[13px] font-semibold uppercase tracking-wider text-brand-200">Live demo</p>
          <h2 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">Try it like your client would</h2>
          <p className="mt-4 text-[17px] leading-relaxed text-slate-300">
            Open our demo furniture store, tap <strong className="text-white">Add Feedback</strong> and click anything. No signup — this is exactly what your clients will see.
          </p>
          <ul className="mt-6 space-y-2 text-slate-300">
            {["Existing feedback markers on the page", "Comment threads and status", "Approve page / request changes", "Works on your phone too"].map((t) => (
              <li key={t} className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-green-400" /> {t}
              </li>
            ))}
          </ul>
          <Button size="xl" className="mt-8" asChild>
            <Link to={DEMO}>
              See Demo <ArrowRight />
            </Link>
          </Button>
        </div>
        <Link to={DEMO} className="group block" aria-label="Open the live demo">
          <div className="overflow-hidden rounded-xl border border-white/10 bg-white/5 p-3 transition-transform group-hover:-translate-y-1">
            <div className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-ink">
              <span className="size-5 rounded bg-brand-600" />
              <span className="text-[13px] font-semibold">ABC Furniture — Staging</span>
              <span className="ml-auto rounded-md bg-brand-600 px-2.5 py-1 text-[12px] font-semibold text-white">+ Add Feedback</span>
              <span className="hidden rounded-md border border-line px-2.5 py-1 text-[12px] sm:inline">Approve Page</span>
            </div>
            <div className="relative mt-3 grid grid-cols-[1fr_120px] gap-3">
              <div className="relative h-64 rounded-lg bg-[#fbf7f1] p-5">
                <div className="h-3 w-24 rounded bg-[#e6d8c4]" />
                <div className="mt-6 h-6 w-4/5 rounded bg-[#d9c6ad]" />
                <div className="mt-2 h-6 w-3/5 rounded bg-[#d9c6ad]" />
                <div className="mt-5 h-8 w-32 rounded bg-[#9a5b2f]" />
                {[
                  ["1", "left-36 top-[136px]", "bg-orange-600"],
                  ["2", "left-6 top-3", "bg-brand-600"],
                  ["3", "left-[60%] top-[200px]", "bg-orange-600"],
                  ["4", "right-6 top-16", "bg-slate-500"],
                ].map(([n, pos, c]) => (
                  <span key={n} className={`absolute ${pos} ${c} flex size-6 items-center justify-center rounded-full rounded-bl-[4px] border-2 border-white text-[11px] font-bold text-white shadow`}>
                    {n}
                  </span>
                ))}
              </div>
              <div className="space-y-2 text-[11px] text-ink">
                {["Change the CTA text", "Logo slightly larger", "Mobile spacing", "Change this image"].map((t, i) => (
                  <div key={t} className="rounded-md bg-white p-2">
                    <span className="font-bold">#{i + 1}</span> {t}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
}

const FEATURES = [
  { icon: MousePointerClick, title: "Click-to-comment", text: "Pin feedback to the exact element — even after the layout changes, we find it again." },
  { icon: Camera, title: "Automatic screenshots", text: "Every comment captures the page as the client saw it, with the click point marked." },
  { icon: Smartphone, title: "Device & browser context", text: "URL, viewport, device, OS, browser and the DOM selector — no more “which phone?”" },
  { icon: SquareKanban, title: "Kanban task board", text: "Open → In Progress → Awaiting Review → Resolved. Drag, assign and prioritise." },
  { icon: MessageCircle, title: "Threads & internal notes", text: "Reply to the client in context. Keep developer notes private with one toggle." },
  { icon: ClipboardCheck, title: "Client approvals", text: "Approve or request changes per version, with name, time and message on record." },
  { icon: Bell, title: "Notifications", text: "In-app, email and WhatsApp alerts for new feedback, replies, fixes and approvals." },
  { icon: Code2, title: "Embeddable widget", text: "A 12 KB async script for WordPress, Shopify or any stack. Shadow DOM — never breaks your CSS." },
  { icon: Users, title: "Roles & permissions", text: "Owner, Admin, Member and Client roles. Clients only ever see their own projects." },
];

function Features() {
  return (
    <Section id="features" eyebrow="Features" title="Everything a revision round needs. Nothing it doesn't." intro="We kept MarkFeed deliberately simple, so your clients actually use it.">
      <div className="grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map((f) => (
          <div key={f.title} className="bg-white p-6">
            <f.icon className="size-5 text-brand-600" />
            <h3 className="mt-4 font-semibold">{f.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{f.text}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

const AUDIENCES = [
  {
    eyebrow: "For agencies",
    title: "Run 20 client projects without 20 WhatsApp groups",
    points: ["One dashboard for every client and website", "Assign feedback to developers & designers", "Client-only access — they never see other clients", "Your brand colour on review links"],
    to: "/for/agencies",
  },
  {
    eyebrow: "For freelancers",
    title: "Look like an agency. Get approvals faster. Get paid sooner.",
    points: ["Send a professional review link instead of screenshots", "Every change request in writing — scope creep, documented", "Client approval on record before the final invoice", "₹499/month — less than one revision call"],
    to: "/for/freelancers",
  },
  {
    eyebrow: "For product teams",
    title: "QA and stakeholder reviews, minus the spreadsheet",
    points: ["Founders, marketing and QA comment on staging", "Bug reports with browser, OS and viewport auto-attached", "Versions per release, feedback per version", "Works with React & Next.js apps"],
    to: "/for/startups",
  },
];

function Audiences() {
  return (
    <section className="border-y border-line bg-white">
      <div className="mx-auto max-w-6xl space-y-16 px-5 py-20 sm:py-24">
        {AUDIENCES.map((a, i) => (
          <div key={a.to} className={`grid items-center gap-10 lg:grid-cols-2 ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
            <div>
              <p className="text-[13px] font-semibold uppercase tracking-wider text-brand-600">{a.eyebrow}</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">{a.title}</h2>
              <Link to={a.to} className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:underline">
                Learn more <ArrowRight className="size-4" />
              </Link>
            </div>
            <ul className="grid gap-3 sm:grid-cols-2">
              {a.points.map((p) => (
                <li key={p} className="flex gap-2.5 rounded-xl border border-line bg-canvas p-4 text-[14.5px] text-slate-700">
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-green-600" /> {p}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function Capabilities() {
  const items = [
    { icon: Globe, title: "Website feedback", text: "Review any live or staging site through a secure link, or install the script for login-protected pages.", to: "/features/website-feedback" },
    ...(FLAGS.mockups ? [{ icon: FileImage, title: "Design & mockup feedback", text: "Upload PNG, JPG, WebP or PDF designs and let clients pin comments before a line of code is written.", to: "/features/mockup-feedback" }] : []),
    { icon: SquareKanban, title: "Task management", text: "Feedback turns into tasks with priority, assignee, due date, attachments and a full activity log.", to: "/features/task-management" },
    { icon: ClipboardCheck, title: "Client approvals", text: "“Everything looks good” or “Changes are required” — captured with person, version and timestamp.", to: "/features/client-feedback" },
  ];
  return (
    <Section eyebrow="One place for the whole review" title={FLAGS.mockups ? "Websites, designs, tasks and sign-off" : "Websites, tasks and sign-off"}>
      <div className="grid gap-4 md:grid-cols-2">
        {items.map((it) => (
          <Link key={it.to} to={it.to} className="group rounded-2xl border border-line bg-white p-7 transition-shadow hover:shadow-md">
            <it.icon className="size-6 text-brand-600" />
            <h3 className="mt-4 text-lg font-semibold group-hover:text-brand-700">{it.title}</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{it.text}</p>
          </Link>
        ))}
      </div>
    </Section>
  );
}

function WhatsAppSection() {
  return (
    <section className="bg-green-50">
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-20 sm:py-24 lg:grid-cols-2">
        <div>
          <p className="text-[13px] font-semibold uppercase tracking-wider text-[#128C7E]">WhatsApp & email</p>
          <h2 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">Meet clients where they already are</h2>
          <p className="mt-4 text-[17px] leading-relaxed text-slate-600">
            Send review requests on WhatsApp with one tap. Customise the message with <code className="rounded bg-white px-1">{"{{client_name}}"}</code>,{" "}
            <code className="rounded bg-white px-1">{"{{project_name}}"}</code> and <code className="rounded bg-white px-1">{"{{review_link}}"}</code>. Get notified when they comment, and let them know when it's fixed.
          </p>
          <p className="mt-3 text-sm text-slate-500">Works with Meta WhatsApp Cloud API, Interakt, Gupshup, Twilio and AiSensy.</p>
        </div>
        <div className="light-island rounded-2xl bg-[#E5DDD5] p-5 shadow-inner">
          <div className="max-w-sm rounded-lg rounded-tl-none bg-white p-4 text-[14px] leading-relaxed text-slate-800 shadow-sm">
            Hi Rahul 👋
            <br />
            <br />
            Your website is ready for review.
            <br />
            <br />
            Please click the link below to review the website and leave your feedback directly on the page.
            <br />
            <br />
            <span className="font-medium text-[#128C7E] underline">Review Website →</span>
            <br />
            <br />
            Thanks!
            <span className="mt-1 block text-right text-[11px] text-slate-400">11:42 am ✓✓</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const seo = SEO_PAGES["/"]!;
  return (
    <>
      <Seo title={seo.title} description={seo.description} path="/" />
      <Hero />
      <Problem />
      <HowItWorks />
      <InteractiveDemo />
      <Features />
      <Audiences />
      <Capabilities />
      <WhatsAppSection />
      <Section id="pricing" eyebrow="Pricing" title="Simple pricing in rupees" intro="Start free. Upgrade when your clients love it.">
        <PricingTable />
      </Section>
      <section className="border-t border-line bg-white">
        <div className="mx-auto max-w-3xl px-5 py-20 sm:py-24">
          <h2 className="text-center text-3xl font-semibold tracking-tight">Questions, answered</h2>
          <div className="mt-10">
            <Faq />
          </div>
        </div>
      </section>
      <FinalCta />
    </>
  );
}

export function FinalCta() {
  return (
    <section className="mx-auto max-w-6xl px-5 py-20">
      <div className="relative overflow-hidden rounded-3xl bg-ink px-6 py-14 text-center text-white sm:px-12">
        <div aria-hidden className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(37,99,235,.45),transparent_60%)]" />
        <div className="relative">
          <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">Your next revision round can be your calmest one.</h2>
          <p className="mx-auto mt-4 max-w-xl text-slate-300">Set up in two minutes. Share one link. Let your clients point at what they want.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button size="xl" asChild>
              <Link to="/signup">
                Start Free <ArrowRight />
              </Link>
            </Button>
            <Button size="xl" variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white" asChild>
              <Link to={DEMO}>See Demo</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
